import styles from "./styles.module.scss";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { getColumnAccessHistory, getColumnUserAccessHistoryFilters } from "../../helpers/apis";
import { RelativeComponentLoader } from "../../components/loader";
import { Sheet, SheetContent, Stack } from "@altimateai/lego";
import { useCallback, useEffect, useState } from "react";
import { Paginate } from "../../components/paginate";
import { DataTable } from "../../components/tables";
import { FilterType, parseSortDirection } from "../../components/tables/types";
import type { ColumnAccessHistoryItem } from "./types";
import ColumnsAccessedSidebar from "./ColumnsAccessedSidebar";
import { UsagePercentageBar } from "./UsagePercentageBar";
import TableHeading from "./TableHeading";
import NoAccessHistory from "./NoAccessHistory";
import useFiltersBySearchParams from "../../helpers/useFiltersBySearchParams";
import { useSearchParamsSync } from "../../components/tables/contexts/useSearchParamsSync";

const GET_ACCESS_HISTORY = "GET-ACCESS-HISTORY";
const GET_ACCESS_HISTORY_FILTERS = "GET-ACCESS-HISTORY-FILTERS";

interface TableAccessHistoryProps {
  tableId: string;
}

const TableAccessHistory = ({ tableId }: TableAccessHistoryProps) => {
  const { updateSearchParamsByKey, searchParams } = useFiltersBySearchParams();

  // State for filters and pagination
  const [columnFilter, setColumnFilterState] = useState<string[]>([]);
  const [page, setPageState] = useState(0);
  const [size, setSizeState] = useState(20);
  const [columnFilterDispTag, setColumnFilterDispTag] = useState<Record<string, string>>({});
  const sortAttribute = searchParams.get("sortAttribute") || "usage";
  const sortOrder = parseSortDirection(searchParams.get("sortOrder"), "desc");
  const [selectedColumn, setSelectedColumn] = useState<ColumnAccessHistoryItem | null>(null);

  // Sync URL params to state on mount
  const onFiltersUpdate = useCallback(
    (filtersToUpdate: Array<{ columnId: string; value: string[] }>) => {
      filtersToUpdate.forEach(({ columnId, value }) => {
        if (columnId === "column_rk") {
          setColumnFilterState(value);
        } else if (columnId === "page") {
          const pageNum = parseInt(value[0] || "1", 10);
          setPageState(pageNum - 1); // Convert to 0-based
        } else if (columnId === "size") {
          const sizeNum = parseInt(value[0] || "20", 10);
          setSizeState(sizeNum);
        }
      });
    },
    []
  );

  const isInitialized = useSearchParamsSync({
    currentFilters: ["column_rk", "page", "size"],
    updateFilters: onFiltersUpdate,
  });

  // Handler to update column filter and sync with URL
  const setColumnFilter = (newFilter: string[]) => {
    updateSearchParamsByKey({
      column_rk: newFilter.length > 0 ? newFilter : undefined,
      page: "1", // Reset to page 1 when filter changes
    });
  };

  // Handler to update page and sync with URL
  const handlePageChange = (newPage: unknown): void => {
    const pageNum = typeof newPage === "number" ? newPage : 0;
    updateSearchParamsByKey({
      page: String(pageNum + 1), // Convert from 0-based to 1-based
    });
  };

  // Handler to update page size and sync with URL
  const handlePageSizeChange = (newSize: unknown): void => {
    const sizeNum = typeof newSize === "number" ? newSize : 20;
    updateSearchParamsByKey({
      size: String(sizeNum),
      page: "1", // Reset to page 1 when size changes
    });
  };

  const getRequestBody = (): Record<string, unknown> => {
    const data: Record<string, unknown> = { table_id: tableId };
    if (columnFilter) data.column_rk = columnFilter;
    if (sortAttribute) {
      data.order_by = sortAttribute;
      data.sort_order = sortOrder;
    }
    return data;
  };

  const { data: columns, isLoading: isColumnsLoading } = useQuery({
    queryKey: [GET_ACCESS_HISTORY, page, size, ...columnFilter, sortOrder, sortAttribute],
    queryFn: () =>
      getColumnAccessHistory({
        page: page + 1,
        size,
        order_by: sortAttribute,
        sort_order: sortOrder,
        ...getRequestBody(),
      }),
    enabled: isInitialized, // Only fetch when URL params are synced
  });

  const { data: columnFilters, isLoading: isColumnFiltersLoading } = useQuery({
    queryKey: [GET_ACCESS_HISTORY_FILTERS, page],
    queryFn: () => getColumnUserAccessHistoryFilters(tableId),
    enabled: isInitialized, // Only fetch when URL params are synced
  });

  useEffect(() => {
    if (columnFilter.length && columnFilters) {
      setColumnFilterDispTag(
        (columnFilters ?? []).reduce(
          (acc: Record<string, string>, { rk, name }: { rk: string; name: string }) =>
            columnFilter.includes(rk) ? { ...acc, [name]: rk } : acc,
          {}
        )
      );
    } else {
      setColumnFilterDispTag({});
    }
  }, [columnFilter, columnFilters]);

  if (!isInitialized || isColumnsLoading || isColumnFiltersLoading) {
    return <RelativeComponentLoader label="Loading access history data" />;
  }
  if (!columns || columns.total === 0) {
    return <NoAccessHistory />;
  }
  return (
    <>
      <Stack
        className={cn(styles.access_history, "al-p-3 al-h-full bg-white border-radius-bottom")}
        direction="column"
      >
        <TableHeading
          numQueries={columns.items[0].queries}
          filters={getRequestBody() as { column_rk?: string[] }}
          filterObj={columnFilterDispTag}
          resetColumnFilter={setColumnFilter}
        />
        <DataTable
          header={[
            {
              id: 1,
              label: "Column",
              uid: "column",
              width: "44%",
              filter: {
                filterType: FilterType.DROPDOWN,
                value: columnFilter,
                options: (columnFilters ?? []).map(t => ({
                  label: t.name,
                  value: t.rk,
                })),
                onChange: setColumnFilter,
              },
              render: (item: ColumnAccessHistoryItem) => (
                <div className="al-break-words al-text-brand al-cursor-pointer">
                  {item.column.name}
                </div>
              ),
            },
            {
              id: 2,
              label: "No. Users",
              uid: "user_count",
              width: "11%",
              sort: {
                sortSearchKey: "sortAttribute",
                sortParamValue: "users",
              },
              render: (item: ColumnAccessHistoryItem) => (
                <div className={"al-break-words"}>{item.user_count}</div>
              ),
            },
            {
              id: 3,
              label: "Queries",
              uid: "column_count",
              width: "11%",
              render: (item: ColumnAccessHistoryItem) => (
                <div className="al-break-words">{item.column_count}</div>
              ),
            },
            {
              id: 4,
              label: "Column Query Usage %",
              uid: "usage",
              width: "34%",
              sort: {
                sortSearchKey: "sortAttribute",
              },
              render: (item: ColumnAccessHistoryItem) => (
                <UsagePercentageBar usagePercentage={item.usage} />
              ),
            },
          ]}
          data={{ items: columns.items }}
          idKey={(item: ColumnAccessHistoryItem) => item.column.rk}
          onRowClick={setSelectedColumn}
          minWidth={700}
          defaultSort={{ sortParamValue: "usage", order: "desc" }}
        />
        <Paginate
          itemCount={columns.total}
          page={page}
          pageSize={columns.size}
          numPages={columns.pages}
          onPageClick={handlePageChange}
          showPageSize={true}
          onPageSizeChange={handlePageSizeChange}
        />
      </Stack>
      <Sheet open={selectedColumn !== null} onOpenChange={open => !open && setSelectedColumn(null)}>
        <SheetContent
          side="right"
          className="!al-max-w-none !al-w-[600px]"
          aria-label="Column access details modal"
        >
          {selectedColumn && <ColumnsAccessedSidebar columnObj={selectedColumn.column} />}
        </SheetContent>
      </Sheet>
    </>
  );
};

export { TableAccessHistory };
